import { ERROR_CODES } from '@roll-web/constants';
import { PAYMENT_METHOD_KV } from '~/lib/payment';
import {
  BuyOrderInput,
  OnErrorCallback,
  OnSuccessCarrierCallback,
  OnSuccessInstantPaymentCallback,
  OnSuccessRedirectCallback,
} from '~/src/services/orders';
import { DeliveryInfo } from '~/types/resources/deliveryInfo';
import type { GachaDetail } from '~/types/resources/gachaDetail';

export type BuildBuyOrderBaseInput = {
  paymentMethod: (typeof PAYMENT_METHOD_KV)[keyof typeof PAYMENT_METHOD_KV];
  gachaDetail: GachaDetail;
  playAmount: number;
  token: string;
  deliveryInfo: DeliveryInfo | null;
  shouldNotDeliver: boolean;
  tippingMessage?: string;
  callbacks: {
    onSuccessInstant: OnSuccessInstantPaymentCallback;
    onSucessRedirect: OnSuccessRedirectCallback;
    onSucessCarrier: OnSuccessCarrierCallback;
    onError: OnErrorCallback;
  };
};
export type BuildBuyOrderWithCreditInput = BuildBuyOrderBaseInput & {
  paymentMethod: typeof PAYMENT_METHOD_KV.credit;
  cardSequence: string;
};
export type BuildBuyOrderWithPlayTicketInput = BuildBuyOrderBaseInput & {
  paymentMethod: typeof PAYMENT_METHOD_KV.playTicket;
  playTicketInput: {
    play_ticket_id: string;
  };
};
export type BuildBuyOrderWithRedirectTypePaymentInput =
  BuildBuyOrderBaseInput & {
    paymentMethod:
      | typeof PAYMENT_METHOD_KV.paypay
      | typeof PAYMENT_METHOD_KV.alipayPlus;
  };
export type BuildBuyOrderWithCarriersInput = BuildBuyOrderBaseInput & {
  paymentMethod:
    | typeof PAYMENT_METHOD_KV.carrieAu
    | typeof PAYMENT_METHOD_KV.carrierDocomo
    | typeof PAYMENT_METHOD_KV.carrierSoftbank
    | typeof PAYMENT_METHOD_KV.unionpay;
};
export type BuildBuyOrderWithApplePayInput = BuildBuyOrderBaseInput & {
  paymentMethod: typeof PAYMENT_METHOD_KV.applepay;
  domainName: string;
  label: string;
  applePaySession: ApplePaySession;
};
export type BuildBuyOrderWithGooglePayInput = BuildBuyOrderBaseInput & {
  paymentMethod: typeof PAYMENT_METHOD_KV.googlepay;
  totalPrice: string;
};

export type BuildBuyOrderInput =
  | BuildBuyOrderWithCreditInput
  | BuildBuyOrderWithPlayTicketInput
  | BuildBuyOrderWithRedirectTypePaymentInput
  | BuildBuyOrderWithCarriersInput
  | BuildBuyOrderWithApplePayInput
  | BuildBuyOrderWithGooglePayInput;

/**
 * BuyOrderInput に変換する
 */
export const buildBuyOrderInput = (
  input: BuildBuyOrderInput
): BuyOrderInput => {
  const {
    paymentMethod,
    gachaDetail,
    playAmount,
    token,
    deliveryInfo,
    shouldNotDeliver,
    callbacks,
    tippingMessage,
  } = input;

  const baseInput = {
    gacha: gachaDetail,
    play_amount: playAmount,
    user_token: token,
    delivery_info: deliveryInfo,
    shouldNotDeliver,
    onErrorCallback: callbacks.onError,
    webroll_live_tipping_message: tippingMessage,
  };

  switch (paymentMethod) {
    case PAYMENT_METHOD_KV.credit: {
      const { cardSequence } = input as BuildBuyOrderWithCreditInput;
      if (cardSequence == null || cardSequence == '')
        throw new Error(ERROR_CODES.CREDIT_CARD_NOT_SELECTED);
      return {
        ...baseInput,
        method: PAYMENT_METHOD_KV.credit,
        cardSeq: cardSequence,
        onSuccessCallback: callbacks.onSuccessInstant,
        onSuccessRedirectCallback: callbacks.onSucessRedirect,
        onErrorCallback: callbacks.onError,
      } as BuyOrderInput;
    }

    case PAYMENT_METHOD_KV.playTicket: {
      const { playTicketInput } = input as BuildBuyOrderWithPlayTicketInput;
      return {
        ...baseInput,
        method: PAYMENT_METHOD_KV.playTicket,
        playTicketInput: {
          id: playTicketInput.play_ticket_id,
        },
        onSuccessCallback: callbacks.onSuccessInstant,
        onErrorCallback: callbacks.onError,
      } as BuyOrderInput;
    }

    case PAYMENT_METHOD_KV.paypay:
    case PAYMENT_METHOD_KV.alipayPlus: {
      return {
        ...baseInput,
        method: paymentMethod,
        onSuccessCallback: callbacks.onSucessRedirect,
        onErrorCallback: callbacks.onError,
      } as BuyOrderInput;
    }
    case PAYMENT_METHOD_KV.applepay: {
      const { domainName, label, applePaySession } =
        input as BuildBuyOrderWithApplePayInput;
      return {
        ...baseInput,
        method: PAYMENT_METHOD_KV.applepay,
        domainName,
        label,
        applePaySession,
        onSuccessCallback: callbacks.onSuccessInstant,
        onErrorCallback: callbacks.onError,
      } as BuyOrderInput;
    }

    case PAYMENT_METHOD_KV.googlepay: {
      const { totalPrice } = input as BuildBuyOrderWithGooglePayInput;
      return {
        ...baseInput,
        method: PAYMENT_METHOD_KV.googlepay,
        totalPrice,
        onSuccessCallback: callbacks.onSuccessInstant,
        onErrorCallback: callbacks.onError,
      } as BuyOrderInput;
    }

    default: {
      return {
        ...baseInput,
        method: paymentMethod as
          | 'carriers:au'
          | 'carriers:docomo'
          | 'carriers:softbank'
          | 'unionpay',
        onSuccessCallback: callbacks.onSucessCarrier,
        onErrorCallback: callbacks.onError,
      };
    }
  }
};
